import React, { useState, useEffect } from 'react';
import { tenantService, userService } from '../services/api';
import { MODULE_LABELS } from './TenantComponents';

const MODULES = ['campaigns', 'devices', 'account'];
const LEVELS = ['admin', 'editor', 'viewer'];

// Create Tenant Modal Component
export const CreateTenantModal = ({ onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Initial members to add (array of { userId, userName, userEmail, permissions })
  //
  // Member grants here are DELIBERATELY unfiltered by enrollment: tenant
  // creation enrolls no products (see POST /api/tenants — the Enrollment card
  // on the tenant detail page is the operator's next click), so at this
  // instant enrolledModules would be ['account'] and filtering would discard
  // the campaigns/devices levels the operator just configured for that
  // imminent enrollment. Grants without enrollment are dormant and harmless:
  // portal gates deny by product, and TenantDetail hides un-enrolled module
  // columns. The same reasoning covers the server-side owner grant (admin on
  // all modules when ownerId is set).
  const [initialMembers, setInitialMembers] = useState([]);
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [addMemberPerms, setAddMemberPerms] = useState({ campaigns: 'viewer', devices: 'viewer', account: 'viewer' });

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await userService.getAll({ search: userSearch, pageSize: 20 });
        setUsers(response.data || []);
      } catch (err) { console.error('Error fetching users:', err); }
      finally { setLoadingUsers(false); }
    };
    fetchUsers();
  }, [userSearch]);

  const handleAddMember = () => {
    if (!addMemberUserId) return;
    const user = users.find(u => u.id === addMemberUserId);
    if (!user) return;
    if (initialMembers.some(m => m.userId === addMemberUserId)) return;
    setInitialMembers(prev => [...prev, {
      userId: addMemberUserId,
      userName: user.name || user.email,
      userEmail: user.email,
      permissions: { ...addMemberPerms },
    }]);
    setAddMemberUserId('');
    setAddMemberPerms({ campaigns: 'viewer', devices: 'viewer', account: 'viewer' });
  };

  const handleRemoveMember = (userId) => {
    setInitialMembers(prev => prev.filter(m => m.userId !== userId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // First owner is the first admin member, if any
      const adminMember = initialMembers.find(m =>
        Object.values(m.permissions).every(p => p === 'admin')
      );
      const payload = {
        name: formData.name,
        description: formData.description || undefined,
        ownerId: adminMember?.userId || undefined,
      };
      const response = await tenantService.create(payload);
      const tenantId = response.data?.id;

      // Add initial members (skip the owner if already assigned via ownerId)
      if (tenantId && initialMembers.length > 0) {
        const failures = [];
        for (const member of initialMembers) {
          if (member.userId === adminMember?.userId) continue; // owner already assigned
          try {
            await tenantService.addUser(tenantId, member.userId, member.permissions);
          } catch (err) {
            failures.push(`${member.userEmail}: ${err.response?.data?.error || err.message}`);
          }
        }
        if (failures.length > 0) {
          onSuccess(`${response.message} (${failures.length} member assignment(s) failed)`);
          return;
        }
      }

      onSuccess(response.message || 'Tenant created successfully');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  const permColors = { admin: '#dc3545', editor: '#fd7e14', viewer: '#6c757d' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px', borderRadius: '12px' }}>
        <div className="modal-header" style={{ borderBottom: '1px solid #e4e6eb', padding: '20px 24px 16px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#1c1e21' }}>Create organization</h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#65676b' }}>Set up a new tenant with optional initial members</p>
          </div>
          <button className="modal-close" onClick={onClose} style={{ background: '#e4e6eb', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.1rem', color: '#4b4f56' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: '20px 24px' }}>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', display: 'block' }}>Tenant name</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                placeholder="Brand or company name"
                minLength={2}
                maxLength={100}
                required
                style={{ borderRadius: '8px', border: '1px solid #dddfe2', padding: '10px 12px', fontSize: '0.95rem', width: '100%', boxSizing: 'border-box' }}
              />
              <span style={{ fontSize: '0.8rem', color: '#65676b', marginTop: '4px', display: 'block' }}>Must be unique across the platform</span>
            </div>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', display: 'block' }}>Description</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
                placeholder="Internal notes about this tenant"
                maxLength={500}
                rows={2}
                style={{ borderRadius: '8px', border: '1px solid #dddfe2', padding: '10px 12px', fontSize: '0.95rem', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>

            {/* Initial Members Section */}
            <div style={{ borderTop: '1px solid #e4e6eb', marginTop: '8px', paddingTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>Initial members</label>
                <span style={{ fontSize: '0.75rem', color: '#65676b' }}>First all-admin member becomes owner</span>
              </div>

              {/* Added Members List */}
              {initialMembers.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  {initialMembers.map(member => (
                    <div key={member.userId} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 12px', background: '#f0f2f5', borderRadius: '8px', marginBottom: '4px',
                    }}>
                      <div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1c1e21' }}>{member.userName}</div>
                        <div style={{ fontSize: '0.8rem', color: '#65676b' }}>{member.userEmail}</div>
                        <div style={{ marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {Object.entries(member.permissions).map(([mod, level]) => (
                            <span key={mod} style={{
                              fontSize: '0.7rem', padding: '2px 6px', borderRadius: '4px',
                              background: `${permColors[level]}15`, color: permColors[level],
                              border: `1px solid ${permColors[level]}30`, textTransform: 'capitalize',
                            }}>
                              {mod}: {level}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button type="button" onClick={() => handleRemoveMember(member.userId)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#65676b', fontSize: '1rem', padding: '4px 8px', borderRadius: '4px' }}
                        onMouseOver={e => e.target.style.background = '#e4e6eb'} onMouseOut={e => e.target.style.background = 'none'}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Member Form */}
              <div style={{ background: '#f0f2f5', borderRadius: '10px', padding: '14px' }}>
                <input
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search users..."
                  style={{ borderRadius: '8px', border: '1px solid #dddfe2', padding: '8px 12px', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', marginBottom: '8px' }}
                />
                {loadingUsers ? (
                  <div style={{ fontSize: '0.85rem', color: '#65676b', padding: '8px 0' }}>Loading...</div>
                ) : (
                  <select value={addMemberUserId} onChange={e => setAddMemberUserId(e.target.value)}
                    style={{ borderRadius: '8px', border: '1px solid #dddfe2', padding: '8px 12px', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', marginBottom: '8px' }}>
                    <option value="">Select user</option>
                    {users.filter(u => !initialMembers.some(m => m.userId === u.id)).map(u => (
                      <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>
                    ))}
                  </select>
                )}
                {addMemberUserId && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#444' }}>Permissions</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {LEVELS.map(l => (
                          <button key={l} type="button"
                            onClick={() => setAddMemberPerms({ campaigns: l, devices: l, account: l })}
                            style={{ fontSize: '0.7rem', padding: '3px 8px', borderRadius: '6px', border: '1px solid #dddfe2', background: '#fff', cursor: 'pointer', textTransform: 'capitalize', color: '#444', fontWeight: 500 }}>
                            All {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {MODULES.map(mod => (
                        <div key={mod} style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize', marginBottom: '4px', color: '#444' }}>{mod}</div>
                          <select value={addMemberPerms[mod]}
                            onChange={e => setAddMemberPerms(prev => ({ ...prev, [mod]: e.target.value }))}
                            style={{ width: '100%', borderRadius: '6px', border: '1px solid #dddfe2', padding: '6px 8px', fontSize: '0.85rem' }}>
                            {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button type="button" onClick={handleAddMember}
                  disabled={!addMemberUserId}
                  style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #dddfe2', background: addMemberUserId ? '#fff' : '#f5f5f5', cursor: addMemberUserId ? 'pointer' : 'default', fontSize: '0.9rem', fontWeight: 500, color: addMemberUserId ? '#1877f2' : '#bec3c9' }}>
                  + Add member
                </button>
              </div>
            </div>
          </div>
          <div className="modal-footer" style={{ borderTop: '1px solid #e4e6eb', padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #dddfe2', background: '#e4e6eb', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500, color: '#444' }}>Cancel</button>
            <button type="submit" disabled={loading || !formData.name} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: loading ? '#a0c4ff' : '#1877f2', color: '#fff', cursor: loading ? 'default' : 'pointer', fontSize: '0.9rem', fontWeight: 600, opacity: !formData.name ? 0.5 : 1 }}>
              {loading ? 'Creating...' : initialMembers.length > 0 ? `Create + ${initialMembers.length} member${initialMembers.length > 1 ? 's' : ''}` : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Tenant Modal Component
export const EditTenantModal = ({ tenant, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({ 
    name: tenant?.name || '', 
    description: tenant?.description || '' 
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await tenantService.update(tenant.id, formData);
      onSuccess(response.message || 'Tenant updated successfully');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to update tenant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Tenant</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Tenant ID</label>
              <input
                type="text"
                value={tenant?.id || ''}
                disabled
                className="input-disabled"
              />
              <span className="form-help">ID cannot be changed</span>
            </div>
            <div className="form-group">
              <label>Tenant Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                placeholder="Enter brand/company name"
                minLength={2}
                maxLength={100}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
                placeholder="Optional internal notes"
                maxLength={500}
                rows={3}
              />
              <span className="form-help">{formData.description?.length || 0}/500 characters</span>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Archive Confirmation Modal
export const ArchiveConfirmModal = ({ tenant, onClose, onConfirm, loading }) => {
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Archive Tenant?</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>Are you sure you want to archive <strong>{tenant?.name}</strong>?</p>
          <ul className="warning-list">
            <li>Tenant will be hidden from the active list</li>
            <li>Users cannot access this tenant's resources</li>
            <li>You can restore this tenant later</li>
          </ul>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Archiving...' : 'Archive Tenant'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Add User to Tenant Modal Component (module-based permissions)
// `enrolledModules` (V276) limits which module rows are shown AND written —
// the submit payload is filtered so hidden modules are never granted viewer
// rows by the state defaults. Defaults to all modules when the prop is absent
// so callers without enrollment context are unaffected.
export const AddUserToTenantModal = ({ tenantId, enrolledModules, onClose, onSuccess, onError }) => {
  const mods = enrolledModules || MODULES;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [permissions, setPermissions] = useState({ campaigns: 'viewer', devices: 'viewer', account: 'viewer' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await userService.getAll({ search: searchTerm, pageSize: 30 });
        setUsers(response.data || []);
      } catch (err) {
        console.error('Error fetching users:', err);
        onError?.('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [searchTerm, onError]);

  const handlePermissionChange = (module, level) => {
    setPermissions(prev => ({ ...prev, [module]: level }));
  };

  const setAllPermissions = (level) => {
    setPermissions(prev => {
      const next = { ...prev };
      mods.forEach(m => { next[m] = level; });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      onError?.('Please select a user');
      return;
    }

    setSubmitting(true);
    try {
      // Only enrolled modules go over the wire — the endpoint upserts exactly
      // what is posted, so omitted modules stay untouched.
      const payload = Object.fromEntries(
        Object.entries(permissions).filter(([m]) => mods.includes(m))
      );
      await tenantService.addUser(tenantId, selectedUserId, payload);
      onSuccess?.('User assigned to tenant successfully');
    } catch (err) {
      onError?.(err.response?.data?.error || 'Failed to assign user to tenant');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add User to Tenant</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Search Users</label>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by email..."
              />
            </div>

            <div className="form-group">
              <label>Select User *</label>
              {loading ? (
                <div className="loading-inline">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="empty-state-small">No users found</div>
              ) : (
                <select
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                  required
                >
                  <option value="">-- Select a user --</option>
                  {users.map(user => (
                    <option key={user.id || user.username} value={user.id}>
                      {user.name || user.email} ({user.email})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label>Module Permissions</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.85rem', color: '#666' }}>Quick set:</span>
                {LEVELS.map(level => (
                  <button key={level} type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setAllPermissions(level)} style={{ textTransform: 'capitalize' }}>
                    All {level}
                  </button>
                ))}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Module</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Permission Level</th>
                  </tr>
                </thead>
                <tbody>
                  {mods.map(mod => (
                    <tr key={mod} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '0.5rem', fontWeight: 500 }}>
                        {MODULE_LABELS[mod] || mod}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <select
                          value={permissions[mod]}
                          onChange={e => handlePermissionChange(mod, e.target.value)}
                          style={{ width: '100%' }}
                        >
                          {LEVELS.map(level => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !selectedUserId}>
              {submitting ? 'Adding...' : 'Add User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
